import React, { useMemo } from 'react'
import { createUseStyles } from 'react-jss'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import {
  getPageId,
  getPageSlices,
  getPageBackgroundColor,
  getProducts,
  getPageMetaData
} from '../redux/slices/content'
import { getCurrentRoutePath } from '../redux/slices/location'
import Footer from './Footer'
import Header from './Header'
import theme from '../style/theme'
import Sidebar from './Sidebar'
import ProductDetailSlice from './Slices/ProductDetailSlice'

import { useSmoothScrollbar } from './useSmoothScrollbar'

import Slices from './Slices'
import CookiesBanner from './CookiesBanner'
import Noise from './Noise'
import ColorContext from './ColorContext'
import detectIt from 'detect-it'

const Main = ({ classes }) => {
  const slices = useSelector(getPageSlices)
  const products = useSelector(getProducts)
  const currentPath = useSelector(getCurrentRoutePath)
  const { ref: scrollRef } = useSmoothScrollbar()
  const id = useSelector(getPageId)

  // Check if this is a product page
  const isProductPage = currentPath.startsWith('/our-products/')
  const productSlug = isProductPage ? currentPath.split('/our-products/')[1] : null

  // Find current product if it's a product page
  const currentProduct = useMemo(() => {
    if (!isProductPage || !productSlug || !products) {
      return null
    }

    const product = products.find(product => {
      const storedSlug = product.store?.slug
      return storedSlug === productSlug
    })

    return product
  }, [isProductPage, productSlug, products])

  // Find CMS page with ProductDetailSlice for current product
  const productCMSData = useMemo(() => {
    if (!isProductPage || !currentProduct || !slices) {
      return null
    }

    // Look for ProductDetailSlice in current page that matches current product
    const productDetailSlice = slices.find(slice => {
      if (slice.type !== 'product_detail_slice') return false

      // Check if this slice's product matches the current product
      const sliceProduct = slice.product
      if (!sliceProduct) return false

      // Compare product IDs or references
      return sliceProduct._id === currentProduct._id ||
             sliceProduct.id === currentProduct.id ||
             sliceProduct.store?.gid === currentProduct.store?.gid
    })

    return productDetailSlice
  }, [isProductPage, currentProduct, slices])

  // DO NOT REMOVE THE USELESS DIV, it is there so that smooth-scrollbar does not crash when it unmounts.
  // See https://github.com/idiotWu/react-smooth-scrollbar/issues/9
  return (
    <main className={classes.main} ref={scrollRef}>
      <div>
        <Noise />
        {!isProductPage && <Slices slices={slices} id={id} key={id} />}
        {isProductPage && currentProduct && (
          <ProductDetailSlice
            slice={{
              product: currentProduct,
              sections: productCMSData?.sections || [] // Use CMS sections if available
            }}
          />
        )}
        <Footer />
      </div>
    </main>
  )
}

const Page = () => {
  const backgroundColor = useSelector(getPageBackgroundColor)
  const borderColor = theme.getBorderColor(backgroundColor)
  const colorContextValue = useMemo(() => ({ backgroundColor, borderColor }), [backgroundColor, borderColor])
  const classes = useStyles({ backgroundColor, borderColor })
  // Get product data for meta tags
  const products = useSelector(getProducts)
  const currentPath = useSelector(getCurrentRoutePath)
  const meta = useSelector(getPageMetaData)

  const isProductPage = currentPath.startsWith('/our-products/')
  const productSlug = isProductPage ? currentPath.split('/our-products/')[1] : null

  const currentProduct = useMemo(() => {
    if (!isProductPage || !productSlug || !products) {
      return null
    }
    return products.find(product => product.store?.slug === productSlug)
  }, [isProductPage, productSlug, products])

  return (
    <ColorContext.Provider value={colorContextValue}>
      <div className={classes.container}>
        <Helmet>
          <title>
            {isProductPage && currentProduct
              ? `${currentProduct.store?.title || currentProduct.title} | Discovered Foods`
              : meta?.title || 'Discovered Foods'}
          </title>
          {isProductPage && currentProduct ? (
            <>
              <meta name='title' content={currentProduct.store?.title || currentProduct.title} />
              <meta name='description' content={currentProduct.store?.descriptionHtml?.replace(/<[^>]+>/g, '').slice(0, 160)} />
              <meta property='og:title' content={currentProduct.store?.title || currentProduct.title} />
              <meta property='og:description' content={currentProduct.store?.descriptionHtml?.replace(/<[^>]+>/g, '').slice(0, 160)} />
              {currentProduct.store?.previewImageUrl && (
                <>
                  <meta property='og:image' content={currentProduct.store.previewImageUrl} />
                  <meta name='twitter:image' content={currentProduct.store.previewImageUrl} />
                </>
              )}
              <meta property='og:type' content='product' />
              <meta name='twitter:card' content='product' />
              <link rel='canonical' href={`${meta?.canonicalUrl || ''}/our-products/${productSlug}`} />
            </>
          ) : (
            <>              <meta name='description' content={meta?.description || 'Default description'} />
              <meta name='keywords' content={meta?.keywords || 'default,keywords'} />
            </>
          )}
        </Helmet>
        <div className={classes.curtain} id='curtain' />
        <Header />
        <Sidebar />
        <Main classes={classes} />
        <CookiesBanner />
      </div>
    </ColorContext.Provider>
  )
}

const useStyles = createUseStyles({
  container: {
    backgroundColor: ({ backgroundColor = theme.colors.backgroundColors.default.background }) => backgroundColor,
    color: ({ backgroundColor }) => theme.getForegroundColor(backgroundColor),
    borderColor: ({ borderColor }) => borderColor
  },
  curtain: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: '100%',
    backgroundColor: theme.colors.primary,
    transform: 'translateY(100%)',
    zIndex: theme.zIndex.pageLoadingBar,
    opacity: 0
  },
  main: {
    ...(detectIt.primaryInput === 'touch' ? {} : {
      overflowY: 'auto',
      overflowX: 'hidden',
      height: '100vh',
      width: '100%'
    })
  }
}, { name: 'App' })

export default Page
